import React, { createContext, useContext, useMemo, useState } from 'react';
import proptypes from 'prop-types';
import { WIDGET_MAPPING } from '../constants';
import { dataModel } from '../dataModel';

const { string, bool, node, shape } = proptypes;

const FitCompatibilityContext = createContext({ type: '', drawerPosition: '' });
export const useFitCompatibility = () => useContext(FitCompatibilityContext);

export const FitCompatibilityProvider = ({
  isLoading,
  type,
  itemId = '',
  navParam = '',
  drawerPosition,
  children = null,
  applianceType = '',
  productData = null,
  enableOptionsThatFitDrawer = false
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInfoIcon, setDrawerInfoIcon] = useState(false);
  const [drawerIndex, setDrawerIndex] = useState(0);
  const [backArrowShown, setBackArrowShown] = useState(null); // Add this state

  const isRefrigerator = applianceType === 'REFRIGERATOR';
  const widgetMapping = applianceType ? WIDGET_MAPPING[applianceType]?.[type] : null;

  const getProductDimensions = () => {
    if (!productData) return null;
    const specificationGroup = productData?.product?.specificationGroup;
    const dimensions = specificationGroup?.find((group) => group.specTitle === 'Dimensions');
    const specifications = dimensions?.specifications;
    const productHeight = specifications?.find((i) => i.specName === 'Assembled Height (in.)' || i.specName === 'Product Height (in.)')?.specValue;
    const productWidth = specifications?.find((i) => i.specName === 'Assembled Width (in.)' || i.specName === 'Product Width (in.)')?.specValue;
    const productDepth = specifications?.find((i) => i.specName === 'Depth (Excluding Handles) (in.)' || i.specName === 'Product Depth (in.)')?.specValue;
    return {
      width: productWidth,
      height: productHeight,
      depth: productDepth
    };
  };

  const getDepthType = () => {
    if (!productData) return null;
    const keyProductFeaturesItems = productData?.product?.keyProductFeatures?.keyProductFeaturesItems?.[0]?.features || [];
    
    const depthType = keyProductFeaturesItems.find((item) => item.name === 'Installation Depth');
    return depthType ? depthType.value : "Standard Depth";
  };

  const productDimensions = getProductDimensions();
  const productDepthType = getDepthType();

  const {
    header = '',
    subtext = '',
    canHaveArrow = false,
    hasInitialBackArrow = false,
    isPip = false,
    isPlp = false,
    isMini = false
  } = widgetMapping || {};

  const value = useMemo(() => ({
    isLoading,
    type,
    itemId,
    navParam,
    drawerPosition,
    header,
    subtext,
    canHaveArrow,
    hasInitialBackArrow,
    isPip,
    isPlp,
    isMini,
    drawerOpen,
    setDrawerOpen,
    drawerInfoIcon,
    setDrawerInfoIcon,
    drawerIndex,
    setDrawerIndex,
    applianceType,
    isRefrigerator,
    productData,
    productDimensions,
    productDepthType,
    enableOptionsThatFitDrawer
  }), [
    isLoading,
    type,
    itemId,
    navParam,
    drawerPosition,
    header,
    subtext,
    canHaveArrow,
    hasInitialBackArrow,
    isPip,
    isPlp,
    isMini,
    drawerOpen,
    setDrawerOpen,
    drawerInfoIcon,
    setDrawerInfoIcon,
    drawerIndex,
    setDrawerIndex,
    applianceType,
    isRefrigerator,
    productData, // Add to dependency array
    productDimensions, // Add to dependency array
    enableOptionsThatFitDrawer
  ]);

  return (
    <FitCompatibilityContext.Provider value={value}>
      {children}
    </FitCompatibilityContext.Provider>
  );
};

FitCompatibilityProvider.propTypes = {
  isLoading: bool.isRequired,
  type: string.isRequired,
  itemId: string,
  navParam: string,
  drawerPosition: string.isRequired,
  children: node,
  applianceType: string,
  productData: shape({}),
  enableOptionsThatFitDrawer: bool
};



FitCompatibilityProvider.dataModel = dataModel;
